import { IPLink, IPStack, IPText } from 'baseComponents';
import { globalFontBoldWeight } from 'globalStyles';
import { LoadingProgressBar } from './LoadingProgressBar';
import { TrendLine } from './TrendLine';

interface IPSeverityBarProps {
  label: string;
  content: string | number;
  color: string;
  width?: number;
  isUpdating?: boolean;
  compact?: boolean;
  showLabelAsLink?: boolean; //When onClick is provided, show label as link
  onClick?: () => void;
  trendLine?: number; // 0 => equal, >0 => higher, <0 => lower
  trendReverse?: boolean; // when true, positive trend is red and negative trend is green
  trendAsPercent?: boolean;
}

export const SeverityBar = (props: IPSeverityBarProps) => {
  const showTrend = props.trendLine !== undefined && !props.isUpdating;

  // Layout tuning
  const contentPadLeft = props.compact ? 12 : 15;
  const trendWidth = 30;

  // If we show the trend, give the number a bit more right padding so text doesn’t collide with the graphic.
  // While updating, keep the loading bar off the right border instead.
  const contentPadRight = showTrend ? trendWidth + 16 : props.isUpdating ? 12 : 0;

  return (
    <IPStack
      tabIndex={props.onClick ? 0 : undefined}
      onClick={props.onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          props.onClick?.();
        }
      }}
      verticalAlign="space-between"
      styles={{
        root: {
          height: props.compact ? 52 : 64,
          width: props.width ?? 100,
          padding: 4,
          position: 'relative',
          border: '1px solid #e1e1e1',
          boxSizing: 'border-box',
          cursor: props.onClick ? 'pointer' : 'default',
        },
      }}
    >
      {/* Vertical bar */}
      <div
        style={{
          position: 'absolute',
          left: 4,
          top: 4,
          bottom: 4,
          width: props.compact ? 5 : 7,
          backgroundColor: props.color,
          borderRadius: 2,
        }}
      />

      {/* Content */}
      <IPStack.Item>
        <IPText
          block
          nowrap
          variant={props.compact ? 'large' : 'xxLarge'}
          styles={{
            root: {
              paddingLeft: contentPadLeft,
              paddingRight: contentPadRight,
              fontWeight: globalFontBoldWeight,
              position: 'relative', // anchor for trend overlay
            },
          }}
        >
          {/* Trendline overlay */}
          {showTrend && (
            <TrendLine
              value={props.trendLine!}
              reverse={props.trendReverse}
              asPercent={props.trendAsPercent}
              width={trendWidth}
              strokeWidth={3}
              right={6}
            />
          )}

          {/* The number itself */}
          <span
            style={{
              position: 'relative',
              zIndex: 1,
              display: props.isUpdating ? 'inline-block' : undefined,
              width: props.isUpdating ? '100%' : undefined,
            }}
          >
            {props.isUpdating ? <LoadingProgressBar barHeight={props.compact ? 2 : 3} /> : props.content}
          </span>
        </IPText>
      </IPStack.Item>

      {/* Label */}
      {props.onClick && props.showLabelAsLink ? (
        <IPStack.Item>
          <IPText
            block
            nowrap
            styles={{
              root: {
                paddingLeft: contentPadLeft,
                paddingTop: 0,
                paddingBottom: 0,
                fontWeight: props.compact ? undefined : globalFontBoldWeight,
              },
            }}
          >
            <IPLink>{props.label}</IPLink>
          </IPText>
        </IPStack.Item>
      ) : (
        <IPStack.Item>
          <IPText
            block
            nowrap
            styles={{
              root: {
                paddingLeft: contentPadLeft,
                paddingTop: 0,
                paddingBottom: 0,
                fontWeight: props.compact ? undefined : globalFontBoldWeight,
              },
            }}
          >
            {props.label}
          </IPText>
        </IPStack.Item>
      )}
    </IPStack>
  );
};
